import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Search, Lock, Unlock, Loader2, MapPin, Calendar, Home, Square, Coins, ShieldCheck, AlertTriangle, Eye, Zap, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { PageHeader } from '@/components/PageHeader';
import { AddressAutocomplete } from '@/components/AddressAutocomplete';
import { ZipStormBadge } from '@/components/ZipStormBadge';
import api from '@/lib/api';
import { refreshCreditCounter } from '@/components/CreditCounter';

const SS_KEY = 'qf:lead-finder:state:v1';

function QualityBadge({ band }) {
    if (!band) return null;
    if (band === 'High') {
        return (
            <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 font-normal" data-testid="lead-quality-high">
                <ShieldCheck className="h-3 w-3 mr-1" /> High quality
            </Badge>
        );
    }
    if (band === 'Medium') {
        return (
            <Badge className="bg-amber-50 text-amber-800 border border-amber-200 font-normal" data-testid="lead-quality-medium">
                <Eye className="h-3 w-3 mr-1" /> Some data
            </Badge>
        );
    }
    return (
        <Badge className="bg-rose-50 text-rose-700 border border-rose-200 font-normal" data-testid="lead-quality-low">
            <AlertTriangle className="h-3 w-3 mr-1" /> Sparse data
        </Badge>
    );
}

function PropertyTile({ p, onUnlock, unlocking }) {
    const masked = !p.unlocked;
    return (
        <Card className={`relative overflow-hidden ${p.unlocked ? 'border-emerald-300' : ''}`} data-testid={`lead-card-${p.cache_key}`}>
            <CardContent className="pt-5 pb-4">
                <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                        <div className="font-display font-semibold text-slate-900 truncate flex items-center gap-1.5">
                            <MapPin className="h-4 w-4 text-slate-500 shrink-0" />
                            <span className="truncate">{masked ? p.addressLine1_masked : (p.address || p.addressLine1_masked)}</span>
                        </div>
                        <div className="text-sm text-slate-500">{p.city}, {p.state} <span className="font-mono">{p.zipCode}</span></div>
                    </div>
                    {p.unlocked ? (
                        <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 font-normal shrink-0">Unlocked</Badge>
                    ) : (
                        <Badge variant="outline" className="bg-slate-50 text-slate-600 shrink-0"><Lock className="h-3 w-3 mr-1" /> Locked</Badge>
                    )}
                </div>

                <div className="mt-2">
                    <QualityBadge band={p.quality_band} />
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-1.5 text-slate-600"><Calendar className="h-3.5 w-3.5" /> {p.yearBuilt || '—'}</div>
                    <div className="flex items-center gap-1.5 text-slate-600"><Home className="h-3.5 w-3.5" /> {p.propertyType || '—'}</div>
                    <div className="flex items-center gap-1.5 text-slate-600"><Square className="h-3.5 w-3.5" /> {p.squareFootage ? `${p.squareFootage.toLocaleString()} sqft` : '—'}</div>
                    <div className="flex items-center gap-1.5 text-slate-600">{p.bedrooms || '—'} bd / {p.bathrooms || '—'} ba</div>
                </div>

                <div className="mt-4 relative z-10">
                    {p.unlocked ? (
                        <Button asChild className="w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid={`lead-open-${p.cache_key}`}>
                            <Link to={`/app/leads/${encodeURIComponent(p.cache_key)}`}>Open lead</Link>
                        </Button>
                    ) : (
                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <Button
                                    className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
                                    data-testid={`lead-unlock-open-${p.cache_key}`}
                                >
                                    <Unlock className="h-4 w-4 mr-1" /> Unlock this lead
                                </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle className="font-display">Unlock this lead?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                        This will use <b>1 lookup</b> to reveal the full address, owner details, year built, property value, and storm context. Crew &amp; Business plans have unlimited lookups.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel data-testid="lead-unlock-cancel-button">Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                        onClick={() => onUnlock(p.cache_key)}
                                        className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
                                        data-testid="lead-unlock-confirm-button"
                                        disabled={unlocking}
                                    >
                                        {unlocking ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Coins className="h-4 w-4 mr-1" />}
                                        Confirm unlock
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    )}
                </div>

                {/* Amber lock overlay over masked content (does NOT block the unlock button below) */}
                {masked && (
                    <div className="qf-lock-overlay" aria-hidden="true" data-testid={`lead-lock-overlay-${p.cache_key}`}>
                        <div className="qf-lock-chip">
                            <Lock className="h-4 w-4" />
                            <span>Tap <b>Unlock this lead</b> below</span>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

const DEFAULT_FILTERS = {
    zip_code: '',
    min_year_built: '',
    max_year_built: '',
    min_value: '',
    max_value: '',
    min_roof_age: '',
    property_type: '',
    include_low_quality: false,
};

function loadPersisted() {
    try {
        const raw = sessionStorage.getItem(SS_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

function persist(state) {
    try {
        sessionStorage.setItem(SS_KEY, JSON.stringify(state));
    } catch {
        // ignore
    }
}

export default function LeadFinderPage() {
    const persisted = loadPersisted();
    const [filters, setFilters] = useState(persisted?.filters || DEFAULT_FILTERS);
    const [loading, setLoading] = useState(false);
    const [unlocking, setUnlocking] = useState(false);
    const [results, setResults] = useState(persisted?.results || null);
    // Direct-address lookup mode
    const [mode, setMode] = useState(persisted?.mode || 'zip');
    const [addressQuery, setAddressQuery] = useState(persisted?.addressQuery || '');
    // Geocoded center returned by Mapbox autocomplete on the last selection;
    // sent to the backend so the lead cache key is canonical.
    const [addressPick, setAddressPick] = useState(null);
    const [addressResult, setAddressResult] = useState(persisted?.addressResult || null);
    const [addressLoading, setAddressLoading] = useState(false);

    // Persist state to sessionStorage so users can navigate to a lead and come back
    useEffect(() => {
        persist({ filters, results, mode, addressQuery, addressResult });
    }, [filters, results, mode, addressQuery, addressResult]);

    const set = (k) => (e) => setFilters((prev) => ({ ...prev, [k]: e?.target ? e.target.value : e }));

    const doSearch = useCallback(async (e) => {
        if (e) e.preventDefault();
        if (!filters.zip_code || filters.zip_code.length < 5) {
            toast.error('Enter a 5-digit ZIP code.');
            return;
        }
        setLoading(true);
        try {
            const payload = {
                zip_code: filters.zip_code.trim(),
                min_year_built: filters.min_year_built ? Number(filters.min_year_built) : null,
                max_year_built: filters.max_year_built ? Number(filters.max_year_built) : null,
                min_value: filters.min_value ? Number(filters.min_value) : null,
                max_value: filters.max_value ? Number(filters.max_value) : null,
                min_roof_age: filters.min_roof_age ? Number(filters.min_roof_age) : null,
                property_type: filters.property_type || null,
                include_low_quality: !!filters.include_low_quality,
                limit: 30,
            };
            // Clean nulls
            Object.keys(payload).forEach((k) => payload[k] == null && delete payload[k]);
            const r = await api.post('/leads/search', payload);
            // Defensive client-side sort: unlocked first (stable)
            const sorted = [...(r.data.results || [])].sort((a, b) => (a.unlocked === b.unlocked ? 0 : a.unlocked ? -1 : 1));
            setResults({ ...r.data, results: sorted });
            const hidden = r.data.hidden_low_quality || 0;
            toast.success(`Found ${r.data.count} properties${hidden ? ` (${hidden} low-quality hidden)` : ''}`);
        } catch (err) {
            const detail = err?.response?.data?.detail || 'Search failed';
            toast.error(detail);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    const doUnlock = async (cache_key) => {
        setUnlocking(true);
        try {
            const r = await api.post('/leads/unlock', { property_key: cache_key });
            const balance = r.data.balance;
            refreshCreditCounter();
            if (r.data.already_unlocked) {
                toast.success('Lead already unlocked.');
            } else if (balance?.unlimited) {
                toast.success('Lead unlocked.');
            } else {
                toast.success(`Lead unlocked! ${balance?.total ?? 0} lookups left this cycle.`);
            }
            // mark the result as unlocked and re-sort
            setResults((prev) => {
                if (!prev) return prev;
                const updated = prev.results.map((p) => (p.cache_key === cache_key ? { ...p, unlocked: true } : p));
                updated.sort((a, b) => (a.unlocked === b.unlocked ? 0 : a.unlocked ? -1 : 1));
                return { ...prev, results: updated };
            });
            // Also update the address-lookup card if it matches
            setAddressResult((prev) => prev && prev.cache_key === cache_key ? { ...prev, unlocked: true } : prev);
        } catch (err) {
            // Don't double-toast 402 — the global paywall modal handles it.
            if (err?.response?.status !== 402) {
                toast.error(err?.response?.data?.detail || 'Unlock failed');
            }
        } finally {
            setUnlocking(false);
        }
    };

    const lookupAddress = async (e, picked) => {
        if (e?.preventDefault) e.preventDefault();
        const q = (picked?.address || addressQuery || '').trim();
        if (q.length < 5) {
            toast.error('Enter a full street address (e.g., 123 Main St, Dallas, TX).');
            return;
        }
        setAddressLoading(true);
        try {
            // If the autocomplete handed us a geocoded center, ship it to the
            // backend so the lead cache key is canonical (same house = same key,
            // regardless of typo/casing/partial typing) and AI Vision starts
            // with a high-quality pin.
            const payload = { address: q };
            if (picked?.center && Array.isArray(picked.center) && picked.center.length === 2) {
                payload.lng = picked.center[0];
                payload.lat = picked.center[1];
            } else if (addressPick?.center) {
                payload.lng = addressPick.center[0];
                payload.lat = addressPick.center[1];
            }
            const r = await api.post('/leads/lookup-address', payload);
            setAddressResult(r.data.property);
            if (!r.data.found_in_records) {
                toast.message('Not in public records — but Mapbox + AI Vision still work on this address.', { description: 'Common for commercial buildings, new construction, and rural lots.' });
            } else {
                toast.success('Address found.');
            }
        } catch (err) {
            if (err?.response?.status !== 402) {
                toast.error(err?.response?.data?.detail || 'Address lookup failed');
            }
        } finally {
            setAddressLoading(false);
        }
    };

    const clearAll = () => {
        setFilters(DEFAULT_FILTERS);
        setResults(null);
        try { sessionStorage.removeItem(SS_KEY); } catch { /* noop */ }
    };

    return (
        <>
            <PageHeader
                title="Lead Finder"
                subtitle="Search by ZIP, or look up any specific address. Unlock leads when you're ready to reach out."
                testId="leads-header"
            />

            <Tabs value={mode} onValueChange={setMode} className="mb-4" data-testid="lead-finder-mode-tabs">
                <TabsList>
                    <TabsTrigger value="zip" data-testid="lead-finder-tab-zip"><Search className="h-4 w-4 mr-1" /> Search by ZIP</TabsTrigger>
                    <TabsTrigger value="address" data-testid="lead-finder-tab-address"><MapPin className="h-4 w-4 mr-1" /> Lookup any address</TabsTrigger>
                </TabsList>

                <TabsContent value="address" className="mt-4">
                    <Card className="mb-6">
                        <CardContent className="pt-5">
                            <form onSubmit={lookupAddress} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                                <div className="md:col-span-9">
                                    <Label htmlFor="addr">Full address</Label>
                                    <AddressAutocomplete
                                        value={addressQuery}
                                        onChange={(v) => { setAddressQuery(v); if (!v) setAddressPick(null); }}
                                        onSelect={(pick) => {
                                            setAddressPick(pick);
                                            // Auto-submit the lookup as soon as the user picks a real suggestion
                                            lookupAddress(null, pick);
                                        }}
                                        placeholder="Start typing a property address..."
                                        disabled={addressLoading}
                                        dataTestId="lead-address-input"
                                    />
                                </div>
                                <div className="md:col-span-3 flex gap-2">
                                    <Button type="submit" disabled={addressLoading} className="bg-slate-900 hover:bg-slate-800 text-white font-semibold w-full" data-testid="lead-address-submit">
                                        {addressLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />} Lookup
                                    </Button>
                                </div>
                                <div className="md:col-span-12 -mt-1 text-xs text-slate-500 flex items-start gap-1.5">
                                    <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                    <span>
                                        Works on <b>any</b> address — homes, commercial buildings, new construction.
                                        Public records load when available (RentCast); the AI estimator runs regardless.
                                    </span>
                                </div>
                            </form>
                        </CardContent>
                    </Card>

                    {addressResult && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="lead-address-result">
                            <PropertyTile p={addressResult} onUnlock={doUnlock} unlocking={unlocking} />
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="zip" className="mt-4">
                    <Card className="mb-6">
                        <CardContent className="pt-5">
                            <form onSubmit={doSearch} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                        <div className="md:col-span-3">
                            <Label htmlFor="zip">ZIP code</Label>
                            <Input id="zip" placeholder="32810" maxLength={5} value={filters.zip_code} onChange={set('zip_code')} className="font-mono" data-testid="lead-search-zip-input" />
                        </div>
                        <div className="md:col-span-2">
                            <Label htmlFor="yfrom">Year built (from)</Label>
                            <Input id="yfrom" placeholder="1980" type="number" value={filters.min_year_built} onChange={set('min_year_built')} data-testid="lead-search-min-year-input" />
                        </div>
                        <div className="md:col-span-2">
                            <Label htmlFor="yto">Year built (to)</Label>
                            <Input id="yto" placeholder="2010" type="number" value={filters.max_year_built} onChange={set('max_year_built')} data-testid="lead-search-max-year-input" />
                        </div>
                        <div className="md:col-span-2">
                            <Label htmlFor="roofage">Min roof age (yrs)</Label>
                            <Input id="roofage" placeholder="15" type="number" value={filters.min_roof_age} onChange={set('min_roof_age')} data-testid="lead-search-min-roofage-input" />
                        </div>
                        <div className="md:col-span-3">
                            <Label htmlFor="ptype">Property type</Label>
                            <Select value={filters.property_type || 'any'} onValueChange={(v) => setFilters({ ...filters, property_type: v === 'any' ? '' : v })}>
                                <SelectTrigger id="ptype" data-testid="lead-search-property-type"><SelectValue placeholder="Any type" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="any">Any type</SelectItem>
                                    <SelectItem value="Single Family">Single Family</SelectItem>
                                    <SelectItem value="Condo">Condo</SelectItem>
                                    <SelectItem value="Townhouse">Townhouse</SelectItem>
                                    <SelectItem value="Apartment">Apartment</SelectItem>
                                    <SelectItem value="Manufactured">Manufactured</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="md:col-span-2">
                            <Label htmlFor="minval">Min value</Label>
                            <Input id="minval" type="number" placeholder="$200,000" value={filters.min_value} onChange={set('min_value')} data-testid="lead-search-min-value-input" />
                        </div>
                        <div className="md:col-span-2">
                            <Label htmlFor="maxval">Max value</Label>
                            <Input id="maxval" type="number" placeholder="$1,000,000" value={filters.max_value} onChange={set('max_value')} data-testid="lead-search-max-value-input" />
                        </div>
                        <div className="md:col-span-5 flex items-center gap-3 px-2 py-2 rounded-md bg-slate-50 border border-slate-200">
                            <Switch
                                id="lowq"
                                checked={!!filters.include_low_quality}
                                onCheckedChange={(v) => setFilters({ ...filters, include_low_quality: !!v })}
                                data-testid="lead-search-include-lowq-toggle"
                            />
                            <Label htmlFor="lowq" className="text-sm text-slate-700 cursor-pointer">
                                Include leads with sparse data <span className="text-slate-500">(off by default to protect your lookups)</span>
                            </Label>
                        </div>
                        <div className="md:col-span-3 flex justify-end gap-2">
                            <Button type="button" variant="outline" onClick={clearAll} data-testid="lead-search-clear-button">Clear</Button>
                            <Button type="submit" disabled={loading} className="bg-slate-900 hover:bg-slate-800 text-white font-semibold" data-testid="lead-search-submit-button">
                                {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />} Search leads
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>

            {results && (
                <>
                    {/* ZIP-level storm summary — fires automatically when we have
                        a ZIP code, gives storm-chaser contractors instant signal
                        on whether the area was hit recently. Compact mode keeps
                        the listing scannable. */}
                    {results.zip_code && (
                        <ZipStormBadge zip={results.zip_code} className="mb-3" />
                    )}
                    <div className="flex items-center justify-between mb-3 text-sm text-slate-600 flex-wrap gap-2">
                        <span data-testid="lead-results-count">
                            Showing {results.count} of {results.total_available} properties in ZIP <b className="font-mono">{results.zip_code}</b>
                            {results.hidden_low_quality > 0 && (
                                <span className="ml-2 text-slate-500">· {results.hidden_low_quality} low-quality hidden</span>
                            )}
                        </span>
                        {results.cached && <Badge variant="outline" className="text-xs">Served from cache</Badge>}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {results.results.map((p) => <PropertyTile key={p.cache_key} p={p} onUnlock={doUnlock} unlocking={unlocking} />)}
                    </div>
                    {results.count === 0 && (
                        <div className="border border-dashed rounded-xl p-8 text-center text-slate-500" data-testid="lead-empty-state">
                            No properties matched. Try widening your filters
                            {!filters.include_low_quality && <> or toggle <b>Include leads with sparse data</b></>}.
                        </div>
                    )}
                </>
            )}
                </TabsContent>
            </Tabs>
        </>
    );
}
